import React, { useState, useEffect } from 'react';
import { NodeService } from '../../services/api';
import { WarehouseNode } from '../../types';
import LoadingIndicator from '../common/LoadingIndicator';
import ErrorDisplay from '../common/ErrorDisplay';
import './SOPDashboard.css';

const SOPDashboard: React.FC = () => {
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('all');
  const [startWeekDate, setStartWeekDate] = useState<Date>(getNextSunday(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseNode[]>([]);
  const [warehouseNames, setWarehouseNames] = useState<string[]>([]);
  const [inboundData, setInboundData] = useState<{ [key: string]: number[] }>({});
  const [inventoryData, setInventoryData] = useState<{ [key: string]: number[] }>({});
  const [outboundData, setOutboundData] = useState<{ [key: string]: number[] }>({});

  // Helper function to get next Sunday
  function getNextSunday(date: Date): Date {
    const nextSunday = new Date(date);
    nextSunday.setDate(date.getDate() + (7 - date.getDay()) % 7);
    return nextSunday;
  }

  // Helper function to format date as YYYY-MM-DD
  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Helper function to generate 13 weeks from start date
  const generate13Weeks = (startDate: Date): string[] => {
    const weeks: string[] = [];
    for (let i = 0; i < 13; i++) {
      const weekDate = new Date(startDate);
      weekDate.setDate(startDate.getDate() + (i * 7));
      weeks.push(formatDateForInput(weekDate));
    }
    return weeks;
  };

  // Helper function to generate random flow values (100-1000, multiples of 50)
  const generateRandomFlow = (): number => {
    const min = 100;
    const max = 1000;
    const step = 50;
    const range = (max - min) / step;
    return min + (Math.floor(Math.random() * (range + 1)) * step);
  };

  // Initialize random data for all warehouses and weeks
  const initializeRandomData = () => {
    const weeks = generate13Weeks(startWeekDate);
    const newInboundData: { [key: string]: number[] } = {};
    const newInventoryData: { [key: string]: number[] } = {};
    const newOutboundData: { [key: string]: number[] } = {};

    warehouseNames.forEach(warehouseName => {
      newInboundData[warehouseName] = weeks.map(() => generateRandomFlow());
      newOutboundData[warehouseName] = weeks.map(() => generateRandomFlow());
      
      // Calculate inventory based on inbound/outbound flows
      const inventory: number[] = [];
      let currentInventory = generateRandomFlow(); // Starting inventory
      
      weeks.forEach((_, index) => {
        inventory.push(currentInventory);
        currentInventory = currentInventory + newInboundData[warehouseName][index] - newOutboundData[warehouseName][index];
        // Ensure inventory doesn't go negative
        if (currentInventory < 0) currentInventory = 0;
      });
      
      newInventoryData[warehouseName] = inventory;
    });

    setInboundData(newInboundData);
    setInventoryData(newInventoryData);
    setOutboundData(newOutboundData);
  };

  useEffect(() => {
    if (warehouseNames.length > 0) {
      initializeRandomData();
    }
  }, [startWeekDate, warehouseNames]);

  // Make S&OP dashboard data available globally for AI Assistant context
  useEffect(() => {
    const dashboardData = {
      selected_warehouse: selectedWarehouse,
      start_week: formatDateForInput(startWeekDate),
      warehouses: warehouseNames,
      planning_weeks: 13,
      weeks: generate13Weeks(startWeekDate),
      inbound_data: inboundData,
      inventory_data: inventoryData,
      outbound_data: outboundData,
      warehouse_nodes: warehouses,
      loading: loading,
      error: error
    };
    (window as any).sopDashboardData = dashboardData;
  }, [selectedWarehouse, startWeekDate, warehouseNames, inboundData, inventoryData, outboundData, warehouses, loading, error]);

  useEffect(() => {
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load warehouse nodes from the warehouse optimization API
      const nodes = await NodeService.getNodes();
      
      // Filter only active nodes
      const activeNodes = nodes.filter(node => node.active !== false);
      
      if (activeNodes.length === 0) {
        console.warn('No active warehouse nodes found, using fallback data');
        // Fallback to demo data if no nodes are available
        const fallbackNodes: WarehouseNode[] = [
          {
            node_id: 'demo-warehouse-a',
            name: 'Warehouse-A',
            location: 'Demo Location A',
            active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            tenant_id: 'demo-tenant'
          },
          {
            node_id: 'demo-warehouse-b', 
            name: 'Warehouse-B',
            location: 'Demo Location B',
            active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            tenant_id: 'demo-tenant'
          },
          {
            node_id: 'demo-warehouse-c',
            name: 'Warehouse-C', 
            location: 'Demo Location C',
            active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            tenant_id: 'demo-tenant'
          }
        ];
        setWarehouses(fallbackNodes);
        setWarehouseNames(fallbackNodes.map(node => node.name));
      } else {
        setWarehouses(activeNodes);
        setWarehouseNames(activeNodes.map(node => node.name));
      }
      
    } catch (err) {
      console.error('Failed to load warehouses from API:', err);
      setError('Failed to load warehouse nodes. Using demo data.');
      
      // Fallback to demo data on error
      const fallbackNodes: WarehouseNode[] = [
        {
          node_id: 'demo-warehouse-a',
          name: 'Warehouse-A',
          location: 'Demo Location A',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          tenant_id: 'demo-tenant'
        },
        {
          node_id: 'demo-warehouse-b',
          name: 'Warehouse-B', 
          location: 'Demo Location B',
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          tenant_id: 'demo-tenant'
        },
        {
          node_id: 'demo-warehouse-c',
          name: 'Warehouse-C',
          location: 'Demo Location C', 
          active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          tenant_id: 'demo-tenant'
        }
      ];
      setWarehouses(fallbackNodes);
      setWarehouseNames(fallbackNodes.map(node => node.name));
    } finally {
      setLoading(false);
    }
  };



  // Handle month/day/year inputs
  const [month, setMonth] = useState(startWeekDate.getMonth() + 1);
  const [day, setDay] = useState(startWeekDate.getDate());
  const [year, setYear] = useState(startWeekDate.getFullYear());

  useEffect(() => {
    setMonth(startWeekDate.getMonth() + 1);
    setDay(startWeekDate.getDate());
    setYear(startWeekDate.getFullYear());
  }, [startWeekDate]);

  const handleDateFromInputs = () => {
    const newDate = new Date(year, month - 1, day);
    const nextSunday = getNextSunday(newDate);
    setStartWeekDate(nextSunday);
  };

  // Render flow tables
  const renderFlowTable = (title: string, data: { [key: string]: number[] }, type: 'inbound' | 'inventory' | 'outbound') => {
    const weeks = generate13Weeks(startWeekDate);
    const colorClass = type === 'inbound' ? 'inbound-table' : type === 'inventory' ? 'inventory-table' : 'outbound-table';
    
    return (
      <div className={`flow-table-container ${colorClass}`}>
        <h4>{title}</h4>
        <div className="flow-table">
          <table>
            <thead>
              <tr>
                <th>Warehouse</th>
                {weeks.map((week, index) => (
                  <th key={week}>Week {index + 1}<br/><small>{new Date(week).toLocaleDateString()}</small></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {warehouseNames.map(warehouseName => (
                <tr key={warehouseName}>
                  <td className="warehouse-cell">{warehouseName}</td>
                  {weeks.map((week, index) => (
                    <td key={`${warehouseName}-${week}`} className="data-cell">
                      {data[warehouseName]?.[index]?.toLocaleString() || 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingIndicator message="Loading S&OP planning data..." />;
  }

  if (error) {
    return (
      <ErrorDisplay 
        error={error} 
        onRetry={loadWarehouses}
        operation="S&OP Dashboard"
      />
    );
  }

  return (
    <div className="sop-dashboard">
      <div className="dashboard-controls">
        <div className="control-group">
          <label htmlFor="warehouse-select">Warehouse:</label>
          <select 
            id="warehouse-select"
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            className="form-select"
          >
            <option value="all">All Warehouses</option>
            {warehouses.map(warehouse => (
              <option key={warehouse.node_id} value={warehouse.node_id}>
                {warehouse.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="control-group">
          <label>Start Week (Sunday):</label>
          <div className="date-inputs">
            <input
              type="number"
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              min="1"
              max="12"
              placeholder="MM"
              className="form-input date-input"
            />
            <input
              type="number"
              value={day}
              onChange={(e) => setDay(parseInt(e.target.value))}
              min="1"
              max="31"
              placeholder="DD"
              className="form-input date-input"
            />
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              min="2020"
              max="2030"
              placeholder="YYYY"
              className="form-input date-input"
            />
            <button 
              onClick={handleDateFromInputs}
              className="btn btn-secondary date-apply-btn"
            >
              Apply
            </button>
          </div>
        </div>
        
        <div className="control-actions">
          <button 
            onClick={() => {
              initializeRandomData();
            }}
            className="btn btn-primary"
          >
            Generate New Data
          </button>
          
          <button 
            onClick={loadWarehouses}
            className="btn btn-secondary"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="flow-tables-section">
          <h3>13-Week Planning Horizon</h3>
          <div className="tables-container">
            {renderFlowTable("Inbound Flows", inboundData, 'inbound')}
            {renderFlowTable("Inventory Levels", inventoryData, 'inventory')}
            {renderFlowTable("Outbound Flows", outboundData, 'outbound')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SOPDashboard;